'use client';

/**
 * @file WebSocketContext — WebSocket 연결 관리 Context
 *
 * 인증 상태에 따라 WebSocket 연결을 자동으로 수립/해제하며,
 * 토픽 구독(subscribe)과 메시지 전송(send) 기능을 하위 컴포넌트에 제공한다.
 * 토큰 만료 시 자동 갱신 후 재연결하는 토큰 프로바이더를 wsManager에 등록한다.
 *
 * 하위 컴포넌트는 `useWebSocket()` 훅을 통해 접근한다.
 *
 * @example
 *   const { isConnected, subscribe, send } = useWebSocket();
 *   const unsub = subscribe('/topic/meeting/123/chat', (data) => { ... });
 *   send('/app/chat/meeting/123', { content: 'Hello' });
 */

import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { wsManager } from '@/lib/websocket';
import { WS_URL } from '@/lib/constants';
import { getAccessToken, isTokenExpired, refreshAccessToken } from '@/lib/auth';
import { useAuth } from '@/hooks/useAuth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WebSocketContextValue {
  /** Whether the WebSocket connection is currently open. */
  isConnected: boolean;

  /**
   * Subscribes to a topic. Returns an unsubscribe function.
   *
   * @param topic   The destination to subscribe to (e.g. `/topic/meeting/123/chat`).
   * @param handler Callback invoked with the parsed message body.
   * @returns       A function that removes the subscription when called.
   */
  subscribe: (topic: string, handler: (data: unknown) => void) => () => void;

  /**
   * Sends a message to a destination.
   *
   * @param destination The destination (e.g. `/app/chat/meeting/123`).
   * @param body        The payload to send (will be JSON-serialized).
   */
  send: (destination: string, body: unknown) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);

  // ---- 인증 상태에 따른 연결 / 해제 ------------------------------------------

  useEffect(() => {
    if (isAuthenticated) {
      // 재연결 시 만료된 토큰을 자동 갱신하는 비동기 토큰 프로바이더 등록
      wsManager.setTokenProvider(async () => {
        const token = getAccessToken();
        if (token && !isTokenExpired(token)) {
          return token;
        }
        // 토큰 만료 또는 부재 — refresh 시도
        const refreshed = await refreshAccessToken();
        return refreshed ? getAccessToken() : null;
      });
      const token = getAccessToken();
      if (token) {
        wsManager.connect(WS_URL, token);
      }
    } else {
      wsManager.disconnect();
    }

    return () => {
      wsManager.disconnect();
    };
  }, [isAuthenticated]);

  // ---- WebSocket 연결 상태 변화 리스너 ---------------------------------------

  useEffect(() => {
    const removeListener = wsManager.onConnectionChange((connected) => {
      setIsConnected(connected);
    });

    // Sync initial state
    setIsConnected(wsManager.isConnected());

    return removeListener;
  }, []);

  // ---- 안정적인 subscribe / send 참조 (리렌더링 시에도 동일 참조 유지) --------

  const subscribe = useCallback((topic: string, handler: (data: unknown) => void): (() => void) => {
    return wsManager.subscribe(topic, handler);
  }, []);

  const send = useCallback((destination: string, body: unknown): void => {
    wsManager.send(destination, body);
  }, []);

  // ---- Memoised context value ---------------------------------------------

  const value = useMemo<WebSocketContextValue>(
    () => ({ isConnected, subscribe, send }),
    [isConnected, subscribe, send],
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}
